import { resolveDispatchDayStatus } from "./dispatchDayControls.js";

const MONTH_LABEL_FORMATTER = new Intl.DateTimeFormat("fr-FR", {
  month: "long",
  year: "numeric",
});

const FULL_DATE_LABEL_FORMATTER = new Intl.DateTimeFormat("fr-FR", {
  day: "numeric",
  month: "long",
  weekday: "long",
  year: "numeric",
});

function padNumber(value) {
  return String(value).padStart(2, "0");
}

export function normalizeText(value) {
  return typeof value === "string" ? value.trim() : "";
}

export function isDispatchDateOutsideRange(dispatchDate, { minDate = "", maxDate = "" } = {}) {
  const date = normalizeText(dispatchDate);
  return Boolean(date && ((minDate && date < minDate) || (maxDate && date > maxDate)));
}

export function toSafeInteger(value) {
  const safeNumber = Number(value);
  return Number.isFinite(safeNumber) ? safeNumber : 0;
}

function parseDispatchDate(value) {
  const normalizedValue = normalizeText(value);
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(normalizedValue);
  if (!match) {
    return null;
  }

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const parsedDate = new Date(year, month - 1, day, 12, 0, 0, 0);

  if (
    Number.isNaN(parsedDate.getTime())
    || parsedDate.getFullYear() !== year
    || parsedDate.getMonth() !== month - 1
    || parsedDate.getDate() !== day
  ) {
    return null;
  }

  return parsedDate;
}

export function normalizeDispatchMonthKey(value) {
  const normalizedValue = normalizeText(value);
  const match = /^(\d{4})-(\d{2})$/.exec(normalizedValue);
  if (!match) {
    return "";
  }

  const month = Number(match[2]);
  if (!Number.isInteger(month) || month < 1 || month > 12) {
    return "";
  }

  return `${match[1]}-${match[2]}`;
}

function buildDispatchDateValue(year, monthIndex, dayNumber) {
  return `${year}-${padNumber(monthIndex + 1)}-${padNumber(dayNumber)}`;
}

export function getDispatchMonthKey(dispatchDate) {
  const parsedDate = parseDispatchDate(dispatchDate);
  if (!parsedDate) {
    return "";
  }

  return `${parsedDate.getFullYear()}-${padNumber(parsedDate.getMonth() + 1)}`;
}

function formatDispatchMonthLabel(monthKey) {
  const [rawYear = "", rawMonth = ""] = String(monthKey).split("-");
  const year = Number(rawYear);
  const month = Number(rawMonth);
  if (!Number.isInteger(year) || !Number.isInteger(month) || month < 1 || month > 12) {
    return monthKey;
  }

  return MONTH_LABEL_FORMATTER.format(new Date(year, month - 1, 1, 12, 0, 0, 0));
}

export function formatReadableDispatchDate(dispatchDate, fallbackLabel = "") {
  const parsedDate = parseDispatchDate(dispatchDate);
  if (!parsedDate) {
    return normalizeText(fallbackLabel) || normalizeText(dispatchDate) || "Journée active";
  }

  return FULL_DATE_LABEL_FORMATTER.format(parsedDate);
}

// Raccourcis clavier globaux du Dispatch : ignorés pendant une saisie ou dans un dialogue/menu.
export function isShortcutTypingTarget(event) {
  return event.defaultPrevented || event.ctrlKey || event.metaKey || event.altKey
    || Boolean(event.target?.closest?.("input, textarea, select, [contenteditable='true'], [role='dialog'], [role='menu'], [role='listbox'], [role='separator'], [role='slider']"));
}

export function getLocalTodayDispatchDate() {
  const currentDate = new Date();
  return buildDispatchDateValue(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate());
}

function getLocalTodayDispatchMonthKey() {
  return getDispatchMonthKey(getLocalTodayDispatchDate());
}

export function buildDispatchMonthGroups(items = []) {
  const monthGroups = new Map();

  items.forEach((item) => {
    const dispatchDate = normalizeText(item?.dispatchDate);
    const monthKey = getDispatchMonthKey(dispatchDate);
    if (!monthKey) {
      return;
    }

    if (!monthGroups.has(monthKey)) {
      monthGroups.set(monthKey, {
        key: monthKey,
        label: formatDispatchMonthLabel(monthKey),
        items: [],
      });
    }

    monthGroups.get(monthKey).items.push(item);
  });

  return [...monthGroups.values()]
    .map((group) => ({
      ...group,
      items: [...group.items].sort((leftItem, rightItem) => {
        const leftValue = normalizeText(leftItem?.dispatchDate);
        const rightValue = normalizeText(rightItem?.dispatchDate);
        return leftValue.localeCompare(rightValue);
      }),
    }))
    .sort((leftGroup, rightGroup) => rightGroup.key.localeCompare(leftGroup.key));
}

export function buildDispatchCalendarCells(monthGroup = null) {
  if (!monthGroup?.key) {
    return [];
  }

  const [rawYear = "", rawMonth = ""] = String(monthGroup.key).split("-");
  const year = Number(rawYear);
  const month = Number(rawMonth);
  if (!Number.isInteger(year) || !Number.isInteger(month) || month < 1 || month > 12) {
    return [];
  }

  const firstDayOfMonth = new Date(year, month - 1, 1, 12, 0, 0, 0);
  const daysInMonth = new Date(year, month, 0, 12, 0, 0, 0).getDate();
  const leadingEmptyCellCount = (firstDayOfMonth.getDay() + 6) % 7;
  const itemsByDispatchDate = new Map(
    monthGroup.items.map((item) => [normalizeText(item?.dispatchDate), item]),
  );
  const cells = [];

  for (let emptyIndex = 0; emptyIndex < leadingEmptyCellCount; emptyIndex += 1) {
    cells.push({
      key: `${monthGroup.key}-empty-start-${emptyIndex}`,
      type: "empty",
    });
  }

  for (let dayNumber = 1; dayNumber <= daysInMonth; dayNumber += 1) {
    const dispatchDate = buildDispatchDateValue(year, month - 1, dayNumber);
    cells.push({
      dayNumber,
      item: itemsByDispatchDate.get(dispatchDate) || null,
      key: `${monthGroup.key}-${dayNumber}`,
      type: "day",
    });
  }

  const trailingEmptyCellCount = (7 - (cells.length % 7)) % 7;
  for (let emptyIndex = 0; emptyIndex < trailingEmptyCellCount; emptyIndex += 1) {
    cells.push({
      key: `${monthGroup.key}-empty-end-${emptyIndex}`,
      type: "empty",
    });
  }

  return cells;
}

export function formatCountLabel(value, singularLabel, pluralLabel = `${singularLabel}s`) {
  const safeValue = toSafeInteger(value);
  return `${safeValue} ${safeValue > 1 ? pluralLabel : singularLabel}`;
}

export function buildDispatchSummary(item = null, { hiddenClosedCount = 0 } = {}) {
  if (!item || typeof item !== "object") {
    return hiddenClosedCount > 0
      ? `Choisissez la journée de dispatch qui pilote l'affectation et l'optimisation. ${hiddenClosedCount} journée${hiddenClosedCount > 1 ? "s" : ""} fermée${hiddenClosedCount > 1 ? "s restent" : " reste"} accessible${hiddenClosedCount > 1 ? "s" : ""}.`
      : "Choisissez la journée de dispatch qui pilote l'affectation, les mutations et l'optimisation.";
  }

  const status = resolveDispatchDayStatus(item);
  const visualStatusKey = status?.legacyKey || "unavailable";
  const requiresRevalidation = status?.requiresRevalidation === true;
  const orderCount = toSafeInteger(item.orderCount);
  const activeOrderCount = toSafeInteger(item.activeOrderCount);
  const readyCount = toSafeInteger(item.readyCount);
  const assignedCount = toSafeInteger(item.assignedCount);
  const summaryParts = [];

  if (orderCount > 0) {
    summaryParts.push(
      activeOrderCount > 0 && activeOrderCount !== orderCount
        ? `${activeOrderCount}/${orderCount} commandes actives`
        : formatCountLabel(orderCount, "commande"),
    );
  } else {
    summaryParts.push("Aucune commande");
  }

  if (item.isInconsistent) {
    summaryParts.push("État mixte à vérifier");
  } else if (readyCount > 0 || assignedCount > 0) {
    summaryParts.push(`${readyCount} prêtes • ${assignedCount} assignées`);
  }

  if (visualStatusKey === "optimized") {
    // Une journée livrée garde le vert « Optimisée », mais « Optimiser » y est grisé.
    summaryParts.push(status?.key === "livre" ? "Journée livrée" : "Optimisation disponible");
  } else if (visualStatusKey === "closed") {
    summaryParts.push("Journée fermée");
  } else if (visualStatusKey !== "inconsistent") {
    summaryParts.push("À optimiser");
  }

  if (requiresRevalidation) {
    summaryParts.push("Revalidation requise");
  }

  if (hiddenClosedCount > 0) {
    summaryParts.push(`${hiddenClosedCount} archive${hiddenClosedCount > 1 ? "s" : ""} accessible${hiddenClosedCount > 1 ? "s" : ""}`);
  }

  return summaryParts.join(" • ");
}

export function buildMonthSummary(monthGroup = null) {
  if (!monthGroup || !Array.isArray(monthGroup.items) || monthGroup.items.length === 0) {
    return "Aucune journée disponible sur ce mois.";
  }

  const dayCount = monthGroup.items.length;
  const closedCount = monthGroup.items.filter((item) => item?.isClosed).length;
  const totalOrders = monthGroup.items.reduce((sum, item) => sum + toSafeInteger(item?.orderCount), 0);
  const summaryParts = [
    formatCountLabel(dayCount, "journée"),
    formatCountLabel(totalOrders, "commande"),
  ];

  if (closedCount > 0) {
    summaryParts.push(`${closedCount} fermée${closedCount > 1 ? "s" : ""}`);
  }

  return summaryParts.join(" • ");
}

export function buildDispatchStatusSummary(items = [], { hiddenClosedCount = 0, showArchive = false } = {}) {
  const summary = {
    closed: 0,
    inconsistent: 0,
    optimized: 0,
    revalidation: 0,
    unoptimized: 0,
  };

  items.forEach((item) => {
    const status = resolveDispatchDayStatus(item);
    const visualStatusKey = status?.legacyKey || "unavailable";

    if (status?.requiresRevalidation === true) {
      summary.revalidation += 1;
    }

    if (visualStatusKey === "optimized") {
      summary.optimized += 1;
      return;
    }

    if (visualStatusKey === "closed") {
      summary.closed += 1;
      return;
    }

    if (visualStatusKey === "inconsistent") {
      // Seules les vraies journées mixtes ; les journées à revalider ont leur propre compte.
      if (item?.isInconsistent) summary.inconsistent += 1;
      return;
    }

    if (visualStatusKey === "unoptimized") {
      summary.unoptimized += 1;
    }
  });

  if (!showArchive) {
    summary.closed += toSafeInteger(hiddenClosedCount);
  }

  return summary;
}

export function buildDaySecondaryLabel(item = null) {
  if (!item || typeof item !== "object") {
    return "";
  }

  const status = resolveDispatchDayStatus(item);
  const visualStatusKey = status?.legacyKey || "unavailable";
  const readyCount = toSafeInteger(item.readyCount);
  const assignedCount = toSafeInteger(item.assignedCount);

  if (visualStatusKey === "inconsistent") {
    return item.isInconsistent && !status?.requiresRevalidation ? "À vérifier" : "À revalider";
  }

  if (visualStatusKey === "closed") {
    return status?.requiresRevalidation === true ? "Fermée • À revalider" : "Fermée";
  }

  if (assignedCount > 0) {
    return `${assignedCount} assign.`;
  }

  if (readyCount > 0) {
    return `${readyCount} prête${readyCount > 1 ? "s" : ""}`;
  }

  if (visualStatusKey === "optimized") {
    return "Snapshot";
  }

  return "À traiter";
}

export function buildDayAriaLabel(item = null, { isToday = false } = {}) {
  if (!item || typeof item !== "object") {
    return "Journée indisponible";
  }

  const status = resolveDispatchDayStatus(item);
  const visualStatusKey = status?.legacyKey || "unavailable";
  const parts = [formatReadableDispatchDate(item.dispatchDate, item.label)];
  parts.push(status.label.toLowerCase());

  const orderCount = toSafeInteger(item.orderCount);
  parts.push(orderCount > 0 ? formatCountLabel(orderCount, "commande") : "aucune commande");

  if (item.isInconsistent) {
    parts.push("état mixte à vérifier");
  } else if (status?.requiresRevalidation === true) {
    parts.push("revalidation requise");
  } else if (visualStatusKey === "optimized") {
    parts.push("snapshot disponible");
  } else if (visualStatusKey === "unoptimized") {
    parts.push("optimisation non lancée");
  }

  if (isToday) {
    parts.push("aujourd'hui");
  }

  return parts.join(", ");
}

export function getStateBadgeClassName(item = null) {
  const status = resolveDispatchDayStatus(item);
  const visualStatusKey = status?.legacyKey || "unavailable";

  if (visualStatusKey === "inconsistent") {
    return "border-[rgba(217,119,6,0.2)] bg-[rgba(255,247,237,0.96)] text-[#9a670f]";
  }

  if (visualStatusKey === "closed") {
    return "border-[rgba(197,82,74,0.18)] bg-[rgba(254,242,242,0.96)] text-[#a03a31]";
  }

  if (visualStatusKey === "optimized") {
    return "border-[rgba(15,118,110,0.18)] bg-[rgba(240,253,250,0.98)] text-[#0f766e]";
  }

  return "border-[rgba(148,163,184,0.22)] bg-[rgba(248,250,252,0.96)] text-slate-600";
}

export function getDayCardClassName(item = null) {
  if (!item || typeof item !== "object") {
    return "border-[rgba(226,232,240,0.68)] bg-[rgba(248,250,252,0.46)] text-slate-300";
  }

  if (item.isActive) {
    return "border-accent bg-accent text-white shadow-[0_16px_30px_rgba(15,92,87,0.18)]";
  }

  const status = resolveDispatchDayStatus(item);
  const visualStatusKey = status?.legacyKey || "unavailable";

  if (visualStatusKey === "inconsistent") {
    return "border-[rgba(217,119,6,0.18)] bg-[rgba(255,251,235,0.96)] text-[#9a670f] hover:border-[rgba(217,119,6,0.28)] hover:bg-[rgba(255,247,237,0.98)]";
  }

  if (visualStatusKey === "closed") {
    return "border-[rgba(197,82,74,0.18)] bg-[rgba(254,242,242,0.96)] text-[#a03a31] hover:border-[rgba(197,82,74,0.28)] hover:bg-[rgba(255,244,243,0.98)]";
  }

  if (visualStatusKey === "optimized") {
    return "border-[rgba(15,118,110,0.16)] bg-[rgba(240,253,250,0.94)] text-[#0f766e] hover:-translate-y-0.5 hover:border-[rgba(15,118,110,0.26)] hover:bg-[rgba(236,253,245,0.98)] hover:shadow-[0_14px_28px_rgba(15,118,110,0.08)]";
  }

  return "border-[rgba(148,163,184,0.2)] bg-white/96 text-slate-700 hover:-translate-y-0.5 hover:border-[rgba(100,116,139,0.26)] hover:bg-white hover:shadow-[0_14px_28px_rgba(15,23,42,0.06)]";
}


export function buildDispatchStatusPills(items = [], { hiddenClosedCount = 0, showArchive = false } = {}) {
  const dispatchStatusSummary = buildDispatchStatusSummary(items, { hiddenClosedCount, showArchive });

  return [
    {
      key: "unoptimized",
      label: formatCountLabel(dispatchStatusSummary.unoptimized, "non optimisée"),
      className: "border-[rgba(148,163,184,0.18)] bg-white/96 text-slate-600",
    },
    {
      key: "optimized",
      label: formatCountLabel(dispatchStatusSummary.optimized, "optimisée"),
      className: "border-[rgba(15,118,110,0.16)] bg-[rgba(240,253,250,0.98)] text-[#0f766e]",
    },
    {
      key: "closed",
      label: formatCountLabel(dispatchStatusSummary.closed, "fermée"),
      className: "border-[rgba(197,82,74,0.16)] bg-[rgba(254,242,242,0.96)] text-[#a03a31]",
    },
    {
      key: "revalidation",
      label: formatCountLabel(dispatchStatusSummary.revalidation, "à revalider", "à revalider"),
      className: "border-[rgba(217,119,6,0.18)] bg-[rgba(255,247,237,0.96)] text-[#9a670f]",
    },
  ];
}

export function resolveDefaultDispatchMonthKey({
  activeDate = "",
  activeDispatchMonth = "",
  items = [],
} = {}) {
  const monthGroups = buildDispatchMonthGroups(items);
  const normalizedActiveDispatchMonth = normalizeDispatchMonthKey(activeDispatchMonth);

  if (normalizedActiveDispatchMonth && monthGroups.some((group) => group.key === normalizedActiveDispatchMonth)) {
    return normalizedActiveDispatchMonth;
  }

  const activeMonthKey = getDispatchMonthKey(activeDate);
  if (activeMonthKey && monthGroups.some((group) => group.key === activeMonthKey)) {
    return activeMonthKey;
  }

  const todayMonthKey = getLocalTodayDispatchMonthKey();
  if (todayMonthKey && monthGroups.some((group) => group.key === todayMonthKey)) {
    return todayMonthKey;
  }

  return monthGroups[0]?.key || "";
}

export function scrollCalendarMonthTabHorizontally(tab, container) {
  if (!tab || !container) return;
  const tabBounds = tab.getBoundingClientRect();
  const containerBounds = container.getBoundingClientRect();
  const left = tabBounds.left - containerBounds.left - container.clientLeft;
  if (left < 0) container.scrollLeft += left;
  else if (left + tabBounds.width > container.clientWidth) container.scrollLeft += left + tabBounds.width - container.clientWidth;
}

export function focusCalendarDay(button) {
  if (!button) return;
  button.focus({ preventScroll: true });
  // Seul le calendrier défile si la journée est masquée dans une petite fenêtre.
  const calendar = button.closest("[data-dispatch-day-calendar-popover], [data-consolidation-calendar]");
  if (!calendar) return;
  const bounds = calendar.getBoundingClientRect();
  const dayBounds = button.getBoundingClientRect();
  const top = bounds.top + calendar.clientTop;
  const bottom = top + calendar.clientHeight;
  if (dayBounds.top < top) calendar.scrollTop += dayBounds.top - top;
  else if (dayBounds.bottom > bottom) calendar.scrollTop += dayBounds.bottom - bottom;
}

export function createDispatchCalendarKeyDownHandler({
  calendarCells = [],
  dayButtonRefs = null,
  onClose = null,
} = {}) {
  return (event) => {
    if (!["ArrowDown", "ArrowLeft", "ArrowRight", "ArrowUp", "End", "Home", "Escape"].includes(event.key)) {
      return;
    }

    if (event.key === "Escape") {
      event.preventDefault();
      event.stopPropagation();
      onClose?.({ restoreFocus: true });
      return;
    }

    const target = event.target;
    if (!(target instanceof HTMLElement)) {
      return;
    }

    const currentIndex = Number(target.dataset.calendarIndex);
    if (!Number.isInteger(currentIndex)) {
      return;
    }

    event.preventDefault();

    const focusableIndices = calendarCells.reduce((indices, cell, index) => {
      if (cell?.item) {
        indices.push(index);
      }

      return indices;
    }, []);

    if (!focusableIndices.length) {
      return;
    }

    const focusIndexByDirection = (startIndex, step) => {
      let nextIndex = startIndex + step;

      while (nextIndex >= 0 && nextIndex < calendarCells.length) {
        if (calendarCells[nextIndex]?.item) {
          focusCalendarDay(dayButtonRefs?.current?.[nextIndex]);
          return;
        }

        nextIndex += step;
      }
    };

    if (event.key === "Home") {
      focusCalendarDay(dayButtonRefs?.current?.[focusableIndices[0]]);
      return;
    }

    if (event.key === "End") {
      focusCalendarDay(dayButtonRefs?.current?.[focusableIndices[focusableIndices.length - 1]]);
      return;
    }

    if (event.key === "ArrowRight") {
      focusIndexByDirection(currentIndex, 1);
      return;
    }

    if (event.key === "ArrowLeft") {
      focusIndexByDirection(currentIndex, -1);
      return;
    }

    if (event.key === "ArrowDown") {
      focusIndexByDirection(currentIndex, 7);
      return;
    }

    if (event.key === "ArrowUp") {
      focusIndexByDirection(currentIndex, -7);
    }
  };
}

export const DISPATCH_DAY_CALENDAR_POPOVER_SELECTOR = "[data-dispatch-day-calendar-popover='true']";

export function isDispatchDayCalendarPopoverTarget(target) {
  return target instanceof Element && Boolean(target.closest(DISPATCH_DAY_CALENDAR_POPOVER_SELECTOR));
}
